import React, { useState, Dispatch, SetStateAction, useCallback } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { FormControl, Link } from '@material-ui/core';
import { handleSignin, handleForgetPassword } from '@app/helpers';
import { SetLoginForm, AuthInfo, TranslationsTypes } from '@app/models';
import { ButtonSubmit, AvatarComponent, ErrorBlock, KaseyaOne, PasslyTextField } from '@app/components';
import { useErrorLoginObserver } from '@app/hooks';
import { UserSessionInfo } from '@app/models';
import { sessionService } from '@app/services';
import { loginForms } from '@app/enums';
import { signInErrorClean } from '@app/store/actions';

type Password = string;
type SetPasword = (psw: Password) => void;
interface IProps {
    setLoginForm: SetLoginForm;
    userSessionInfo: UserSessionInfo;
    setAuthInfo: Dispatch<SetStateAction<AuthInfo>>;
    isKaseyaOneLoginEnabled?: boolean;
    handleKaseyaOneClick?: () => void;
    errorKaseyaOneCode: TranslationsTypes;
    isLoading?: boolean;
}

const handleChangePassword =
    (setCredentials: SetPasword) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        setCredentials(value);
    };

export const SignInSelected: React.SFC<IProps> = ({
    userSessionInfo,
    setLoginForm,
    setAuthInfo,
    isKaseyaOneLoginEnabled,
    handleKaseyaOneClick,
    errorKaseyaOneCode,
    isLoading
}) => {
    const [password, setPassword] = useState<Password>('');
    const [errorCode, setErrorCode] = useState<TranslationsTypes>('');
    const [isError, setIsError] = useState<boolean>(false);
    const [emptyPasswordError, setEmptyPasswordError] = useState(false);
    const loggingIn = useSelector((state: any) => state.signIn.loggingIn);

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    useErrorLoginObserver(setIsError, () => {}, setLoginForm, setAuthInfo, setErrorCode);
    const { username, displayInfo, userUuid } = userSessionInfo;

    const selectAnotherClicked = useCallback(() => {
        dispatch(signInErrorClean());

        if (sessionService.isUserHaveSessions() && sessionService.getSessionHistory().length > 1) {
            setLoginForm(loginForms.list);
        } else {
            setLoginForm(loginForms.unknown);
        }
    }, []);

    return (
        <div className='loginSelected'>
            <h1>{`${translation('SIGNIN_HI')} ${displayInfo}`}</h1>
            <ErrorBlock className='loginSelected__error' isError={isError || !!errorKaseyaOneCode} errorCode={errorCode || errorKaseyaOneCode} />
            <div className='loginSelected__username'>
                <AvatarComponent className='loginSelected__username-icon' userId={userUuid} size='xs' imageType='blue' />
                {username}
            </div>
            <form
                onSubmit={handleSignin({
                    username,
                    password,
                    setIsError,
                    setErrorCode,
                    dispatch,
                    setEmptyPasswordError
                })}
            >
                <FormControl fullWidth>
                    <Link className='login__forgot-password' onClick={handleForgetPassword(setLoginForm)} underline='none'>{`${translation(
                        'NEED_HELP'
                    )}?`}</Link>
                    <PasslyTextField
                        autoFocus
                        label={'PASSWORD'}
                        value={password}
                        type='password'
                        onChange={handleChangePassword(setPassword)}
                        id='userPassword-input'
                        error={emptyPasswordError}
                        helperText={emptyPasswordError && translation('EMPTY_FIELD_ERROR')}
                        fullWidth
                    />
                </FormControl>
                <div className='loginSelected__buttons'>
                    <ButtonSubmit
                        className='loginSelected__buttons-login'
                        isDisabled={loggingIn}
                        isLoad={loggingIn}
                        buttonText={translation('SIGNIN_BUTTON')}
                        id='signinSelected'
                        disableRipple={true}
                    />
                </div>
            </form>
            <div className='loginSelected__footer'>
                <Link
                    className='loginSelected__button-other'
                    id='otherSigninOptionsButton'
                    color='primary'
                    underline='none'
                    onClick={selectAnotherClicked}
                >
                    {translation('SIGNIN_OPTIONS_OTHER')}
                </Link>
            </div>
            {isKaseyaOneLoginEnabled && <KaseyaOne onClick={handleKaseyaOneClick} isLoading={isLoading} />}
        </div>
    );
};
